import {css, StyleSheet} from 'aphrodite';
import React, {useMemo} from 'react';

import {WarehouseModelData, WarehouseRelationshipData} from '../warehouseModelsDataLayout';
import {useDiagramLayoutContext} from './DiagramLayoutContext';
import WarehouseDiagramRelationship from './WarehouseDiagramRelationship';

export interface WarehouseDiagramRelationshipSVGProps {
  models: WarehouseModelData;
}

export default function WarehouseDiagramRelationshipSVG(
  props: WarehouseDiagramRelationshipSVGProps
): JSX.Element {
  const {
    layout: {hoveredRelationship},
    shouldShowRelationship,
    getRelationshipPath,
  } = useDiagramLayoutContext();

  const {hoveredDiagramRelationship, otherDiagramRelationships} = useMemo(() => {
    const displayedRelationships = props.models.relationships.filter((relationship) =>
      shouldShowRelationship(relationship, props.models)
    );

    // The hovered relationship needs to be rendered last, or parts of it that overlap other relationships
    // may be obscured by those relationships. The below code moves the hovered relationship (if there is
    // one) from wherever it is in the list of relationships to the end of the list of relationships.
    const hoveredIndex = hoveredRelationship ? displayedRelationships.indexOf(hoveredRelationship) : -1;

    let hoveredRelationshipData: WarehouseRelationshipData | null;
    if (hoveredIndex >= 0) {
      [hoveredRelationshipData] = displayedRelationships.splice(hoveredIndex, 1);
    } else {
      hoveredRelationshipData = null;
    }

    return {
      hoveredDiagramRelationship: hoveredRelationshipData != null && (
        <WarehouseDiagramRelationship
          relationshipData={hoveredRelationshipData}
          path={getRelationshipPath(hoveredRelationshipData)}
          hovered={true}
          key={hoveredRelationshipData.id}
        />
      ),
      otherDiagramRelationships: displayedRelationships.map((relationshipData: WarehouseRelationshipData) => (
        <WarehouseDiagramRelationship
          relationshipData={relationshipData}
          path={getRelationshipPath(relationshipData)}
          hovered={false}
          key={relationshipData.id}
        />
      )),
    };
  }, [props.models, hoveredRelationship, getRelationshipPath, shouldShowRelationship]);

  return (
    <svg className={css(styles.svg)}>
      <>
        {otherDiagramRelationships}
        {hoveredDiagramRelationship}
      </>
    </svg>
  );
}

const styles = StyleSheet.create({
  svg: {
    width: '100%',
    height: '100%',
    overflow: 'visible',
    cursor: 'move',
  },
});
